import React, { ReactElement } from 'react'
import { ISubscriptionEvent, ISubscriptionEventMessage } from '@vacationtracker/shared/types/notification'
import { Button, Progress } from 'antd'
import { EventTypeEnum } from '@vacationtracker/shared/types/core-event'
import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'
import { showErrors } from '@vacationtracker/shared/components/toil-form/errors'
import { ToilRequestErrorTypeEnum } from '@vacationtracker/shared/types/toil-request'
import { NUMBER_OF_LOCATIONS_LIMIT, NUMBER_OF_TEAMS_LIMIT } from '@vacationtracker/shared/data/app-parameters'
import { AutomationWarningCode, automationRuleNameIds, automationWarningCodes } from '@vacationtracker/shared/types/calculations'

// Third kind of "Notification", event success/failure on the dashboard
interface INotificationInfoResponse {
  action: string
  notifications: string[] | object
  options: {
    key: string
    message: string
    title?: string
    titleValues?: { [key: string]: any }
    description?: string | object
    descriptionValues?: { [key: string]: any }
    btn?: ReactElement
    duration: number
    updateNotification: boolean
    forceErrorMessage?: boolean
  }
}

// DUPLICATE IN SHARED

/** How a tracked event ended: reported done, reported failed, or not reported in time. */
export type EventOutcome = 'success' | 'error' | 'timeout'

export interface WatchEventOptions {
  /** Announce only a failure: the caller shows a success itself. */
  quiet?: boolean
  timeoutMs: number
}

interface EventWatcher {
  quiet: boolean
  settle: (outcome: EventOutcome) => void
}

const eventWatchers = new Map<string, EventWatcher>()

// A reply can arrive before the poster has its correlation id back, so outcomes are kept briefly
// for a watch registered late.
const SETTLED_TTL_MS = 30_000
const settledEvents = new Map<string, { outcome: EventOutcome; at: number }>()

function dropExpiredSettled(now: number) {
  settledEvents.forEach((entry, id) => {
    if (now - entry.at > SETTLED_TTL_MS) settledEvents.delete(id)
  })
}

function settle(correlationId: string, outcome: EventOutcome) {
  const now = Date.now()
  dropExpiredSettled(now)
  settledEvents.set(correlationId, { outcome, at: now })
  eventWatchers.get(correlationId)?.settle(outcome)
}

// The holidays tab watches these, and its success can come back before it has tracked the id.
// Their handlers also reject with a bare error code, which would otherwise be shown as it is.
const HOLIDAY_EVENTS = ['LOCATION_HOLIDAY_UPDATED', 'LOCATION_HOLIDAY_DELETED', 'LOCATION_HOLIDAYS_DELETED']

/** Keeps the outcome of an event the Notifications container does not track, for a watch registered after it. */
export function recordUntrackedOutcome(correlationId: string, eventType: string, outcome: EventOutcome): void {
  if (!correlationId || !HOLIDAY_EVENTS.includes(eventType)) return
  settle(correlationId, outcome)
}

/** The outcome already reported for this correlation id, if it was reported recently. */
export function settledOutcome(correlationId: string): EventOutcome | undefined {
  dropExpiredSettled(Date.now())
  return settledEvents.get(correlationId)?.outcome
}

/**
 * Resolves when the event posted under this correlation id leaves the tracked action notifications,
 * which generateNotificationInfo decides: on its first success that would be announced, or on a
 * failure. Track the id with addActionNotifications as well, unless settledOutcome already has it.
 */
export function watchEvent(correlationId: string, { quiet = false, timeoutMs }: WatchEventOptions): Promise<EventOutcome> {
  const early = settledOutcome(correlationId)
  if (early) return Promise.resolve(early)
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      eventWatchers.delete(correlationId)
      resolve('timeout')
    }, timeoutMs)
    eventWatchers.set(correlationId, {
      quiet,
      settle: (outcome) => {
        clearTimeout(timer)
        eventWatchers.delete(correlationId)
        resolve(outcome)
      },
    })
  })
}

const HOLIDAY_ERROR_KEYS: Record<string, string> = {
  HolidayUpdateImpossible: 'holidaysTab.error.HolidayUpdateImpossible',
  HolidayNotInValidYear: 'holidaysTab.error.HolidayNotInValidYear',
  HolidayStartAndEndInDifferentYears: 'holidaysTab.error.HolidayStartAndEndInDifferentYears',
  HolidayStartAfterEnd: 'holidaysTab.error.HolidayStartAfterEnd',
  HolidayNotFound: 'holidaysTab.error.HolidayNotFound',
  HolidayDatesOverlap: 'holidaysTab.error.HolidayDatesOverlap',
  InvalidHolidayForYear: 'holidaysTab.error.InvalidHolidayForYear',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drainGroupProgress(notifications: any, correlationId: string, remainingUsers: unknown[]): { totalUsers: number; done: boolean; percent: number } {
  const groupEvent = notifications[correlationId]
  groupEvent.payload.users = remainingUsers
  const totalUsers = groupEvent.payload.totalUsers
  const percent = Math.round((100 / totalUsers) * (totalUsers - remainingUsers.length))
  const done = remainingUsers.length === 0
  if (done) {
    delete notifications[correlationId]
  }
  return { totalUsers, done, percent }
}

export function generateNotificationInfo(event: ISubscriptionEvent, notifications: string[] | object, formatMessage): INotificationInfoResponse {
  let action = event.code || 'open'
  let newAction

  let subscriptionEvent: ISubscriptionEventMessage | null = null
  const message: string = event.message
  try {
    subscriptionEvent = JSON.parse(event.originalEvent)
  } catch (err) {
    // Generic message
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let result: any = null
  if (event?.result) {
    try {
      result = JSON.parse(event.result)
    } catch (error) {
      // Generic message
    }
  }

  let title
  let titleValues = {}
  let description
  let updateNotification = true
  let forceErrorMessage = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const descriptionValues: any = {}
  let button: ReactElement | undefined
  let key = subscriptionEvent?.correlationId || ''

  if (action === 'error') {
    const errorCode = event.errorCode
    switch (subscriptionEvent?.eventType) {
      case 'BILLING_UPDATED':
        if (message.includes('Subscription is not active') || message.includes('Invalid operation as underlying resource is not active')) {
          title = 'billing.subscriptionExpired'
          description = 'billing.subscriptionExpiredDescription'
        }
        if (message.includes('Patch cannot be completed')) {
          title = 'billing.subscriptionUpdateInProgress'
          description = 'billing.subscriptionUpdateInProgressDescription'
        }
        if (errorCode === 'PaymentFailed') {
          title = 'error.paymentFailed'
          description = 'stripe.dynamicMessage'
          descriptionValues.message = JSON.parse(event.data).message
        } else if (errorCode === 'PromoCodeIsntValid') {
          title = 'error.PromoCodeIsntValid'
          titleValues = JSON.parse(event.data)
          forceErrorMessage = true
        } else if (errorCode === 'PromoCodeDoesntExist') {
          // We don't want to show that the promo code doesn't exist, and give the possibility to hack the promo code.
          title = 'error.PromoCodeIsntValid'
          titleValues = JSON.parse(event.data)
          forceErrorMessage = true
        } else if (errorCode === 'TeamLimitReached') {
          title = 'error.TeamLimitReached'
          titleValues = {
            limitNumber: NUMBER_OF_TEAMS_LIMIT,
          }
        } else if (errorCode === 'LocationLimitReached') {
          title = 'error.LocationLimitReached'
          titleValues = {
            limitNumber: NUMBER_OF_LOCATIONS_LIMIT,
          }
        } else if (errorCode === 'DowngradeImpossibleCustomWorkDay') {
          title = 'error.CustomWorkday'
        } else if (errorCode) {
          title = `error.${errorCode}`
          if (JSON.parse(event.data)) {
            const data = JSON.parse(event.data)
            if (data.promoCode) {
              titleValues = data
            }
          }
        }
        break
      case 'LOCATION_USER_MOVED':
      case 'TEAM_USER_MOVED': {
        if (!Array.isArray(notifications) && notifications[subscriptionEvent.correlationId]) {
          const groupEvent = notifications[subscriptionEvent.correlationId]
          groupEvent.payload.totalUsers -= 1
          drainGroupProgress(
            notifications,
            subscriptionEvent.correlationId,
            groupEvent.payload.users.filter((id) => id !== subscriptionEvent?.userId)
          )
        }
        break
      }
      case 'LOCATION_YEAR_HOLIDAYS_CHANGED':
        if (errorCode === 'InvalidHolidayForYear') {
          const date = event.message.match(/\d{4}-\d{2}-\d{2}/) as string[]
          title = 'error.holidayDateInvalid'
          titleValues = {
            date: date[0],
            year: event.message.slice(-4),
          }
          forceErrorMessage = true
        }
        break
      case 'LOCATION_HOLIDAY_CREATED':
      case 'LOCATION_HOLIDAY_UPDATED':
        if (errorCode === 'HolidayDatesOverlap') {
          const holiday = JSON.parse(event.data)
          title =
            subscriptionEvent.eventType === 'LOCATION_HOLIDAY_CREATED'
              ? 'components.createHolidaysForm.createHolidayErrorOverlap'
              : 'components.createHolidaysForm.updateHolidayErrorOverlap'
          titleValues = {
            holidayName: holiday?.name,
            holidayDate: new Date(`${holidayDay(holiday?.date ?? '')}T00:00:00`),
          }
        }
        break
      case 'LEAVE_TYPE_CREATED':
        if (notifications[`${subscriptionEvent.correlationId}#LEAVE_POLICY_ENABLED`]) {
          key = `${subscriptionEvent.correlationId}#LEAVE_POLICY_ENABLED`
          newAction = 'close'
          title = 'error.generic'
          delete notifications[`${subscriptionEvent.correlationId}#LEAVE_POLICY_ENABLED`]
        }
        break
      case 'LEAVE_TYPE_UPDATED':
        if (notifications[subscriptionEvent.correlationId]) {
          key = subscriptionEvent.correlationId
          newAction = 'close'
          title = 'error.generic'
          delete notifications[subscriptionEvent.correlationId]
        }
        break
      case 'TOIL_REQUEST_CREATED':
      case 'TOIL_REQUEST_ADDED':
      case 'TOIL_REQUEST_DENIED':
      case 'TOIL_REQUEST_APPROVED':
      case 'TOIL_REQUEST_UPDATED':
      case 'TOIL_REQUEST_DELETED':
      case 'TOIL_REQUEST_CANCELLED':
        title = showErrors(event.message as ToilRequestErrorTypeEnum)
        break
      case 'LEAVE_REQUEST_CREATED':
      case 'LEAVE_REQUEST_UPDATED':
        // These handlers reject the request with the bare automation code as the error message,
        // which would otherwise be shown to the user as is.
        if (automationWarningCodes.includes(message as AutomationWarningCode)) {
          title = automationRuleNameIds[message as AutomationWarningCode]
          description = 'automations.requestBlockedByAutomation'
        }
        break
      case 'BLACKOUT_PERIOD_DELETED':
        title = 'error.blackOutPeriod.isntDeleted'
        description = `error.${errorCode}`
        break
      case 'BLACKOUT_PERIOD_CREATED':
        title = 'error.blackOutPeriod.isntCreated'
        description = `error.${errorCode}`
        break
      case 'BLACKOUT_PERIOD_UPDATED':
        title = 'error.blackOutPeriod.isntUpdated'
        description = `error.${errorCode}`
        break
      case 'MAXIMUM_USERS_AWAY_DELETED':
        title = 'error.maxmimumUsersAway.isntDeleted'
        description = `error.${errorCode}`
        break
      case 'MAXIMUM_USERS_AWAY_CREATED':
        title = 'error.maxmimumUsersAway.isntCreated'
        description = `error.${errorCode}`
        break
      case 'MAXIMUM_USERS_AWAY_UPDATED':
        title = 'error.maxmimumUsersAway.isntUpdated'
        description = `error.${errorCode}`
        break
      case 'SENIORITY_ENTITLEMENT_DELETED':
        title = 'error.seniorityEntitlement.isntDeleted'
        description = `error.${errorCode}`
        break
      case 'SENIORITY_ENTITLEMENT_CREATED':
        title = 'error.seniorityEntitlement.isntCreated'
        description = `error.${errorCode}`
        break
      case 'SENIORITY_ENTITLEMENT_UPDATED':
        title = 'error.seniorityEntitlement.isntUpdated'
        description = `error.${errorCode}`
        break
      case 'ENTITLEMENT_BY_ROLE_DELETED':
        title = 'error.entitlementByRole.isntDeleted'
        description = `error.${errorCode}`
        break
      case 'ENTITLEMENT_BY_ROLE_CREATED':
        title = 'error.entitlementByRole.isntCreated'
        description = `error.${errorCode}`
        break
      case 'ENTITLEMENT_BY_ROLE_UPDATED':
        title = 'error.entitlementByRole.isntUpdated'
        description = `error.${errorCode}`
        break
      case 'OPEN_API_ADDON_CREATED':
        title = 'error.openApi.isntCreated'
        description = `error.${errorCode}`
        break
      case 'OPEN_API_ADDON_DELETED':
        title = 'error.openApi.isntDeleted'
        description = `error.${errorCode}`
        break
      case 'TEAM_DELETED':
        title = 'error.team.delete'
        description = `error.${errorCode}`
        break
      case 'PROBATION_PERIOD_DELETED':
        title = 'error.probationPeriod.isntDeleted'
        description = `error.${errorCode}`
        break
      case 'PROBATION_PERIOD_CREATED':
        title = 'error.probationPeriod.isntCreated'
        description = `error.${errorCode}`
        break
      case 'PROBATION_PERIOD_UPDATED':
        title = 'error.probationPeriod.isntUpdated'
        description = `error.${errorCode}`
        break
      case 'LEAVE_DURATION_LIMIT_DELETED':
        title = 'error.leaveDurationLimit.isntDeleted'
        description = `error.${errorCode}`
        break
      case 'LEAVE_DURATION_LIMIT_CREATED':
        title = 'error.leaveDurationLimit.isntCreated'
        description = `error.${errorCode}`
        break
      case 'LEAVE_DURATION_LIMIT_UPDATED':
        title = 'error.leaveDurationLimit.isntUpdated'
        description = `error.${errorCode}`
        break
      case 'LEAVE_REQUEST_NOTICE_PERIOD_DELETED':
        title = 'error.leaveRequestNoticePeriod.isntDeleted'
        description = `error.${errorCode}`
        break
      case 'LEAVE_REQUEST_NOTICE_PERIOD_CREATED':
        title = 'error.leaveRequestNoticePeriod.isntCreated'
        description = `error.${errorCode}`
        break
      case 'LEAVE_REQUEST_NOTICE_PERIOD_UPDATED':
        title = 'error.leaveRequestNoticePeriod.isntUpdated'
        description = `error.${errorCode}`
        break
      case 'USER_UPDATED':
        title = `error.${errorCode}`
        break
    }
    if (!title && HOLIDAY_EVENTS.includes(subscriptionEvent?.eventType as string)) {
      title = 'error.generic'
      const knownKey = errorCode ? HOLIDAY_ERROR_KEYS[errorCode] : undefined
      if (knownKey) {
        description = knownKey
      } else if (message && message !== errorCode) {
        // An object: a string description is read as a message id, and the server's message has none.
        description = <>{message}</>
      }
    }
  }

  if (action !== 'error') {
    switch (subscriptionEvent?.eventType) {
      case 'LOCATION_CREATED':
        button = (
          <Button type="primary" size="small" href={`/app/settings/locations/${subscriptionEvent?.locationId}/general`}>
            {formatMessage({ id: 'actionNotifications.location.view' })}
          </Button>
        )
        title = 'actionNotifications.location.createdTitle'
        titleValues = { locationName: subscriptionEvent.name }
        break

      case 'LOCATION_UPDATED':
        button = (
          <Button type="primary" size="small" href={`/app/settings/locations/${subscriptionEvent?.locationId}/general`}>
            {formatMessage({ id: 'actionNotifications.location.view' })}
          </Button>
        )
        title = 'actionNotifications.location.updatedTitle'
        titleValues = { locationName: subscriptionEvent.name }
        break

      case 'LOCATION_DELETED':
        title = 'actionNotifications.location.deletedTitle'
        break

      case 'TEAM_CREATED':
        button = (
          <Button type="primary" size="small" href={`/app/settings/departments/${subscriptionEvent?.teamId}`}>
            {formatMessage({ id: 'departments.viewDepartment' })}
          </Button>
        )
        title = 'actionNotifications.team.createdTitle'
        titleValues = { teamName: subscriptionEvent.name }
        break

      case 'TEAM_UPDATED':
        button = (
          <Button type="primary" size="small" href={`/app/settings/departments/${subscriptionEvent?.teamId}`}>
            {formatMessage({ id: 'departments.viewDepartment' })}
          </Button>
        )
        title = 'actionNotifications.team.updatedTitle'
        titleValues = { teamName: subscriptionEvent.name }
        break

      case 'TEAM_DELETED':
        title = 'actionNotifications.team.deletedTitle'
        break

      case 'LEAVE_REQUEST_APPROVED':
        title = 'actionNotifications.leave.requestApproved'
        break

      case 'LEAVE_REQUEST_STATUS_CHANGED':
        if (subscriptionEvent.leaveStatus === 'DENIED') {
          title = 'actionNotifications.leave.requestDenied'
        } else {
          title = 'actionNotifications.leave.requestApproved'
        }
        break

      case 'LEAVE_REQUEST_DENIED':
        if (result && result?.isEdited && result?.originalVersion.status === 'APPROVED') {
          title = 'actionNotifications.leave.editedRequestDenied'
          description = 'actionNotifications.leave.editedRequestDeniedDescription'
        } else {
          title = 'actionNotifications.leave.requestDenied'
        }
        break

      case 'NOTIFICATION_BIRTHDAY_CREATED':
      case 'NOTIFICATION_CREATED':
        title = 'actionNotifications.notification.created'
        break

      case 'NOTIFICATION_BIRTHDAY_UPDATED':
      case 'NOTIFICATION_UPDATED':
        title = 'actionNotifications.notification.updated'
        break

      case 'LEAVE_TYPE_UPDATED':
        title = 'actionNotifications.leaveType.updated'
        titleValues = { leaveTypeName: subscriptionEvent.name }
        break

      case 'LEAVE_TYPE_CREATED':
        title = 'actionNotifications.leaveType.created'
        titleValues = { leaveTypeName: subscriptionEvent.name }
        break

      case 'LEAVE_TYPE_DELETED':
        title = 'actionNotifications.leaveType.deleted'
        break

      case 'LEAVE_POLICY_ENABLED':
        if (Array.isArray(notifications)) {
          title = 'components.locationLeavePolicy.successTitle'
        } else {
          let correlationId = subscriptionEvent.correlationId
          let currentEvent = notifications[subscriptionEvent.correlationId]
          if (!currentEvent) {
            currentEvent = notifications[`${subscriptionEvent.correlationId}#${subscriptionEvent.eventType}`]
            correlationId = `${subscriptionEvent.correlationId}#${subscriptionEvent.eventType}`
            key = correlationId
          }
          currentEvent.payload.locationIds = currentEvent.payload.locationIds.filter((event) => event !== subscriptionEvent?.locationId)

          const totalLocationIds = currentEvent.payload.totalLocationIds
          const procent = (100 / totalLocationIds) * (totalLocationIds - currentEvent.payload.locationIds.length)

          description = <Progress percent={Math.round(procent)} size="small" />

          if (currentEvent.payload.locationIds.length === 0) {
            delete notifications[correlationId]
            title =
              totalLocationIds > 1 ? 'components.createLeaveType.assignToLocationManyInProgressCompleted' : 'components.createLeaveType.assignToLocationOneInProgressCompleted'
          } else {
            notifications[correlationId] = currentEvent
            title = totalLocationIds > 1 ? 'components.createLeaveType.assignToLocationManyInProgress' : 'components.createLeaveType.assignToLocationOneInProgress'
          }
        }
        break

      case 'IMPORT_USERS':
        if (result?.skipped?.length) {
          action = 'warning'
          title = 'users.importPartialSuccessTitle'
          titleValues = { imported: result.imported, total: result.imported + result.skipped.length }
          description = 'users.importPartialSuccessDescription'
          descriptionValues.count = result.skipped.length
          descriptionValues.code = key
          // The import is done, but skipped users never emit a progress event - clear the
          // stuck in-progress notification so the bar can't linger or be re-rendered.
          if (!Array.isArray(notifications)) {
            delete notifications[key]
          }
        } else {
          title = 'users.importedSuccessfully'
        }
        break

      case 'LEAVE_REQUEST_CANCELLED':
        title = 'user.cancelLeaveRequestTitle'
        break

      case 'LEAVE_REQUEST_ADDED':
        title = 'addLeave.title'
        break

      case 'LEAVE_REQUEST_UPDATED':
        title = 'editLeave.title'
        break

      case 'LEAVE_REQUEST_CREATED':
        title = 'requestLeave.title'
        break

      case 'TOIL_REQUEST_CREATED':
        title = 'components.toil.requested'
        break

      case 'TOIL_REQUEST_ADDED':
        title = 'components.toil.added'
        break

      case 'TOIL_REQUEST_APPROVED':
        title = 'components.toil.approved'
        break

      case 'TOIL_REQUEST_DENIED':
        title = 'components.toil.denied'
        break

      case 'TOIL_REQUEST_UPDATED':
        title = 'components.toil.edited'
        break

      case 'TOIL_REQUEST_DELETED':
        title = 'components.toil.deleted'
        break

      case 'TOIL_REQUEST_CANCELLED':
        title = 'components.toil.cancelled'
        break

      case 'LOCATION_YEAR_HOLIDAYS_CHANGED':
        title = 'location.holidays.successTitle'
        break
      case 'LOCATION_HOLIDAYS_IMPORTED':
        title = 'location.holidays.successTitle'
        break
      case 'LOCATION_HOLIDAY_UPDATED':
        title = 'components.createHolidaysForm.updateHolidaySuccess'
        break
      case 'LOCATION_HOLIDAY_DELETED':
        title = 'location.holidays.deletedSuccessTitle'
        break
      case 'LOCATION_HOLIDAYS_DELETED':
        title = 'location.holidays.bulkDeletedSuccessTitle'
        break
      case 'LOCATION_HOLIDAY_CREATED':
        title = 'components.createHolidaysForm.createHolidaySuccess'
        break

      case 'USER_UPDATED':
        title = 'user.updateSuccessTitle'
        break

      case 'USERS_INVITED':
        title = 'user.invitationSuccessTitle'
        break

      case 'USER_INVITATION_DELETED':
        title = 'user.invitationDeletedTitle'
        break

      case 'USER_STATUS_CHANGED':
        if (Array.isArray(notifications)) {
          title = 'user.status.updateSuccessTitle'
        } else {
          const currentEvent = notifications[subscriptionEvent.correlationId]
          // We don't have slack id in subscriptionEvent.
          const remaining =
            subscriptionEvent.platform === 'slack' ? currentEvent.payload.users.slice(0, -1) : currentEvent.payload.users.filter((event) => event !== subscriptionEvent?.userId)
          const { totalUsers, done, percent } = drainGroupProgress(notifications, subscriptionEvent.correlationId, remaining)
          action = done ? 'success' : 'open'
          description = <Progress percent={percent} size="small" />
          if (currentEvent.payload.type === 'assignLicenses') {
            title = done ? 'manageMicrosoftLicenses.assignLicencesSuccessTitle' : 'manageMicrosoftLicenses.weAreAssignLicenses'
          } else if (done) {
            title = totalUsers > 1 ? 'users.importMany.successTitle' : 'users.importOne.successTitle'
          } else {
            title = totalUsers > 1 ? 'users.importMany.InProgress' : 'users.importOne.InProgress'
          }
        }
        break

      case 'USER_LEAVE_TYPES_UPDATED':
        title = 'users.updateQuota.successTitle'
        break

      case 'LOCATION_USER_MOVED':
      case 'TEAM_USER_MOVED': {
        const isTeamMove = subscriptionEvent.eventType === 'TEAM_USER_MOVED'
        const groupEvent = !Array.isArray(notifications) ? notifications[subscriptionEvent.correlationId] : undefined
        if (!groupEvent) {
          title = isTeamMove ? 'user.team.moveSuccessTitle' : 'user.location.moveSuccessTitle'
          titleValues = { name: subscriptionEvent.name }
          break
        }
        const remaining = groupEvent.payload.users.filter((id) => id !== subscriptionEvent?.userId)
        const { totalUsers, done, percent } = drainGroupProgress(notifications, subscriptionEvent.correlationId, remaining)
        action = done ? 'success' : 'open'
        description = <Progress percent={percent} size="small" />
        titleValues = { count: totalUsers, name: groupEvent.payload.name }
        title = done ? (isTeamMove ? 'users.table.bulkMoveTeamSuccess' : 'users.table.bulkMoveLocationSuccess') : 'users.table.bulkMoveInProgress'
        break
      }

      case 'LEAVE_REQUEST_DELETED':
        title = 'user.deleteLeaveSuccessTitle'
        break

      case 'USER_WORK_WEEK_UPDATED':
        title = 'user.workWeek.updateSuccessTitle'
        break

      case 'SYNC_EXISTING_USERS':
        title = 'users.syncExistingUsers.successTitle'
        break

      case 'BULK_USER_STATUS':
        title = 'user.bulkChangeStatusSuccessTitle'
        break

      case 'NOTIFICATION_DELETED':
      case 'NOTIFICATION_BIRTHDAY_DELETED':
        title = 'actionNotifications.notification.deleted'
        break

      case 'COMPANY_SETTINGS_UPDATED':
        title = 'general.updateSuccessTitle'
        break

      case 'COMPANY_PLATFORM_CHANGED':
        title = 'general.updateSuccessTitle'
        break

      case 'BILLING_UPDATED':
        title = 'billing.updatedSuccessfully'
        if (result && result?.subscriptionStatus === 'canceled') {
          title = 'billing.subscriptionExpired'
          description = 'billing.subscriptionExpiredDescription'
          action = 'error'
        }
        //TODO: Check is this if work, on line 36 we have if with (action !== error)
        if (subscriptionEvent.stripeCouponId && action === 'error') {
          description = 'billing.promoCodeErrorDescription'
          descriptionValues.code = subscriptionEvent.stripeCouponId
        }
        break

      case 'SUBSCRIPTION_CANCELED':
        title = 'billing.subscriptionCanceled'
        break

      case 'PAYMENT_PROCESSOR_UPDATED':
        // this event is triggred together with BILLING_UPDATED and we want to avoid showing a success message before the other event is finished
        // BILLING_UPDATED fails if payment is not processed
        title = 'billing.updatedSuccessfully'
        updateNotification = false
        break

      case 'SCHEDULED_REPORT_CREATED':
        button = (
          <Button type="primary" size="small" href={`/app/reports/scheduled-reports/${subscriptionEvent?.scheduledReportId}/edit`}>
            {formatMessage({ id: 'actionNotifications.scheduledReport.viewScheduledReport' })}
          </Button>
        )
        title = 'actionNotifications.scheduledReport.createdTitle'
        titleValues = { scheduledReportName: subscriptionEvent.name }
        break

      case 'SCHEDULED_REPORT_UPDATED':
        button = (
          <Button type="primary" size="small" href={`/app/reports/scheduled-reports/${subscriptionEvent?.scheduledReportId}/edit`}>
            {formatMessage({ id: 'actionNotifications.scheduledReport.viewScheduledReport' })}
          </Button>
        )
        title = 'actionNotifications.scheduledReport.updatedTitle'
        titleValues = { scheduledReportName: subscriptionEvent.name }
        break

      case 'SCHEDULED_REPORT_DELETED':
        title = 'actionNotifications.scheduledReport.deletedTitle'
        break

      case 'ANNOUNCE_VT_COMPANY':
        title = 'actionNotifications.companyAnnouncement.success'
        break

      case EventTypeEnum.USER_OUT_OF_OFFICE_SETTINGS_UPDATED:
        title = 'actionNotifications.outOfOfficeSetting.success'
        break

      case 'BLACKOUT_PERIOD_CREATED':
        title = 'automations.blackoutPeriod.createInProgressCompleted'
        break

      case EventTypeEnum.MAXIMUM_USERS_AWAY_CREATED:
        title = 'automations.maximumUsersAway.createInProgressCompleted'
        break

      case EventTypeEnum.MAXIMUM_USERS_AWAY_UPDATED:
        title = 'automations.maximumUsersAway.updateInProgressCompleted'
        break

      case 'OPEN_API_ADDON_CREATED':
        title = 'addons.openApi.createInProgressCompleted'
        break

      case 'OPEN_API_ADDON_DELETED':
        title = 'addons.openApi.openApiDeleted'
        break

      case 'BLACKOUT_PERIOD_UPDATED':
        title = 'automations.blackoutPeriod.updateInProgressCompleted'
        break

      case 'PROBATION_PERIOD_CREATED':
        title = 'automations.probationPeriod.createInProgressCompleted'
        break

      case 'PROBATION_PERIOD_UPDATED':
        title = 'automations.probationPeriod.updateInProgressCompleted'
        break

      case 'BLACKOUT_PERIOD_DELETED':
        title = 'automations.automationBlackoutPeriodDeleted'
        break

      case EventTypeEnum.MAXIMUM_USERS_AWAY_DELETED:
        title = 'utomations.maximumUsersAway.maximumUsersAwayDeleted'
        break

      case 'SENIORITY_ENTITLEMENT_DELETED':
        title = 'automations.automationSeniorityEntitlementDeleted'
        break

      case 'ENTITLEMENT_BY_ROLE_DELETED':
        title = 'automations.automationEntitlementByRoleDeleted'
        break

      case 'PROBATION_PERIOD_DELETED':
        title = 'automations.automationProbationPeriodDeleted'
        break

      case 'SENIORITY_ENTITLEMENT_CREATED':
        title = 'automations.createdAndActive'
        titleValues = { name: subscriptionEvent.name }
        break

      case 'SENIORITY_ENTITLEMENT_UPDATED':
        title = 'automations.seniorityEntitlement.updateInProgressCompleted'
        break

      case 'ENTITLEMENT_BY_ROLE_CREATED':
        title = 'automations.entitlementByRole.createInProgressCompleted'
        break

      case 'ENTITLEMENT_BY_ROLE_UPDATED':
        title = 'automations.entitlementByRole.updateInProgressCompleted'
        break

      case 'VISIBILITY_ADDON_UPDATED':
        title = 'automations.visibility.updateInProgressCompleted'
        break
      case 'VISIBILITY_ADDON_CREATED':
        title = 'automations.visibility.createInProgressCompleted'
        break

      case EventTypeEnum.LEAVE_DURATION_LIMIT_DELETED:
        title = 'automations.leaveDurationLimit.deleted'
        break
      case EventTypeEnum.LEAVE_DURATION_LIMIT_CREATED:
        title = 'automations.leaveDurationLimit.createInProgressCompleted'
        break
      case EventTypeEnum.LEAVE_DURATION_LIMIT_UPDATED:
        title = 'automations.leaveDurationLimit.updateInProgressCompleted'
        break

      case EventTypeEnum.LEAVE_REQUEST_NOTICE_PERIOD_DELETED:
        title = 'automations.leaveRequestNoticePeriod.deleted'
        break
      case EventTypeEnum.LEAVE_REQUEST_NOTICE_PERIOD_CREATED:
        title = 'automations.leaveRequestNoticePeriod.createInProgressCompleted'
        break
      case EventTypeEnum.LEAVE_REQUEST_NOTICE_PERIOD_UPDATED:
        title = 'automations.leaveRequestNoticePeriod.updateInProgressCompleted'
        break

      case 'ADDONS_UPDATED':
        if (subscriptionEvent.isActive) {
          title = 'automations.addpnSuccessfullyAdded'
          description = 'automations.addpnSuccessfullyAddedDescription'
        } else {
          title = 'automations.addpnSuccessfullyDeactivated'
          description = 'automations.addpnSuccessfullyDeactivatedDescription'
        }
        break
    }
  }

  // Settled only where the id really leaves the tracked list: the Notifications container keeps it
  // for a success with no title, which a sibling event under the same id may still need.
  if (key && (action === 'error' || (title && updateNotification))) {
    const outcome: EventOutcome = action === 'error' ? 'error' : 'success'
    if (outcome === 'success' && eventWatchers.get(key)?.quiet) {
      newAction = 'close'
    }
    settle(key, outcome)
  }

  const response: INotificationInfoResponse = {
    action: newAction ? newAction : action,
    notifications,
    options: {
      key,
      message,
      duration: action === 'success' ? 10 : 0,
      updateNotification,
      forceErrorMessage,
    },
  }

  if (key) {
    response.options.key = key
  }

  if (title) {
    response.options.title = title
    response.options.titleValues = titleValues
  }

  if (description) {
    response.options.description = description
    response.options.descriptionValues = descriptionValues
  }

  if (button) {
    response.options.btn = button
  }

  if (Array.isArray(notifications)) {
    notifications = notifications.filter((value) => value !== key)
  }

  response.notifications = notifications

  return response
}

const numberToDay = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
}

export const getNumberToDay = (day, formatMessage) => {
  const mappedDay = numberToDay[day]
  return {
    full: formatMessage({ id: `app.${mappedDay}` }),
    short: formatMessage({ id: `app.${mappedDay}.short` }),
  }
}
